import { create } from 'zustand';

/**
 * Rascunhos do compositor, por conversa — igual ao WhatsApp: o que o atendente
 * digitou numa conversa continua lá quando ele volta depois de abrir outras, o
 * card mostra "Rascunho: …" e a conversa sobe na lista como se tivesse acabado
 * de enviar (na hora em que o rascunho foi deixado). Apagou o rascunho, o card
 * volta pra posição de origem.
 *
 * Vive SÓ EM MEMÓRIA, de propósito (decisão do dono, 20/set): um F5 ou fechar
 * a aba apaga tudo. Rascunho é da tela, não do atendimento — não vai pro
 * servidor nem pro storage do navegador. Chave = id do ticket. Teto de 200
 * conversas (os mais antigos caem) pra uma aba aberta o dia inteiro não
 * acumular sem fim.
 *
 * É um store (Zustand) e não um Map solto porque a LISTA precisa reagir: o
 * card e a ordenação assinam o rascunho do ticket deles.
 */

const MAXIMO = 200;

export interface Rascunho {
  texto: string;
  /** Quando foi deixado — é a "hora do envio" que posiciona a conversa na lista. */
  em: number;
}

interface RascunhosStore {
  porTicket: Record<number, Rascunho>;
  salvar: (ticketId: number, texto: string, agora?: number) => void;
  limparTudo: () => void;
}

export const useRascunhos = create<RascunhosStore>((set) => ({
  porTicket: {},
  salvar: (ticketId, texto, agora = Date.now()) =>
    set((s) => {
      const atual = s.porTicket[ticketId];
      if (texto.trim().length === 0) {
        if (!atual) return s;
        const { [ticketId]: _fora, ...resto } = s.porTicket;
        return { porTicket: resto };
      }
      // Mesmo texto de antes: não mexe (nem na hora — a conversa não "reenvia").
      if (atual && atual.texto === texto) return s;
      const proximo: Record<number, Rascunho> = { ...s.porTicket, [ticketId]: { texto, em: agora } };
      const ids = Object.keys(proximo).map(Number);
      if (ids.length > MAXIMO) {
        ids.sort((a, b) => proximo[a]!.em - proximo[b]!.em);
        for (const id of ids.slice(0, ids.length - MAXIMO)) delete proximo[id];
      }
      return { porTicket: proximo };
    }),
  limparTudo: () => set({ porTicket: {} }),
}));

export function lerRascunho(ticketId: number): string {
  return useRascunhos.getState().porTicket[ticketId]?.texto ?? '';
}

/** Texto vazio = apaga o rascunho (enviou, ou limpou a caixa). */
export function salvarRascunho(ticketId: number, texto: string, agora?: number): void {
  useRascunhos.getState().salvar(ticketId, texto, agora);
}

/** Só pra teste: zera tudo. */
export function limparRascunhos(): void {
  useRascunhos.getState().limparTudo();
}

/**
 * Reposiciona na lista as conversas com rascunho "como se tivessem acabado de
 * enviar": cada uma sai de onde está e entra antes da primeira conversa cuja
 * última mensagem é mais antiga que o rascunho. Sem rascunho, a lista é a do
 * servidor — apagou o rascunho, a conversa volta pra origem. Puro, pra testar.
 */
export function ordenarComRascunhos<T extends { id: number; last_message_at: string }>(
  lista: readonly T[],
  rascunhos: Record<number, Rascunho>,
): T[] {
  const comRascunho = lista.filter((t) => rascunhos[t.id]);
  if (comRascunho.length === 0) return [...lista];
  const resultado: T[] = lista.filter((t) => !rascunhos[t.id]);
  // Mais antigo primeiro: inserindo do mais velho pro mais novo, o mais novo
  // acaba acima quando dois rascunhos disputam a mesma vaga.
  comRascunho.sort((a, b) => rascunhos[a.id]!.em - rascunhos[b.id]!.em);
  for (const t of comRascunho) {
    const em = rascunhos[t.id]!.em;
    const pos = resultado.findIndex((o) => {
      const dele = rascunhos[o.id]?.em ?? new Date(o.last_message_at).getTime();
      return dele < em;
    });
    resultado.splice(pos === -1 ? resultado.length : pos, 0, t);
  }
  return resultado;
}
